/**
 * Beacon brand marks for the product UI shell.
 * Geometric Beacon B mark (`/beacon-logo-b.svg`) — node-hex with lettermark B.
 * Wordmark stylized as BeaCon — capital C inside an AWS gold (#FF9900) circle; Bea/on in navy or surface text.
 * No "for AWS", no AWS smile. Legacy AgentPack/AP files live under frontend/brand-archive/ (not deployed).
 */

type MarkProps = {
  size?: number
  className?: string
  title?: string
}

/** Node-hex B lettermark (AgentPack marketplace mark, AP→B only). */
export function BeaconMark({ size = 32, className, title = 'BeaCon' }: MarkProps) {
  return (
    <img
      src="/beacon-logo-b.svg"
      alt={title}
      width={size}
      height={size}
      className={className}
      draggable={false}
    />
  )
}

/** Square mark sized for login / auth chrome (alias of BeaconMark). */
export function BeaconLogo({ size = 84, className, title = 'BeaCon' }: MarkProps) {
  return <BeaconMark size={size} className={className} title={title} />
}

type LockupProps = {
  height?: number
  className?: string
  title?: string
}

/** Horizontal lockup: node-hex B mark + BeaCon wordmark with gold-circled C (viewBox 280×80). */
export function BeaconLockup({ height = 40, className, title = 'BeaCon' }: LockupProps) {
  const width = Math.round((height * 280) / 80)
  return (
    <img
      src="/beacon-logo-lockup.svg"
      alt={title}
      width={width}
      height={height}
      className={className}
      draggable={false}
    />
  )
}

type WordmarkProps = {
  className?: string
  /** Color classes for “Bea”, “C”, and “on”. Gold circle uses border-beacon-orange (#FF9900). */
  textClassName?: string
  title?: string
}

/**
 * Stylized product wordmark: BeaCon — capital C inside an AWS gold circle.
 * Distinctive mark is the gold ring around C (not yellow-only letter fill).
 * Use for sidebar, login headers, marketplace chrome, and other brand lockups.
 */
export function BeaconWordmark({
  className = '',
  textClassName = 'text-beacon-navy',
  title = 'BeaCon',
}: WordmarkProps) {
  return (
    <span
      className={`inline-flex items-baseline ${className}`}
      title={title}
      aria-label={title}
    >
      <span className={textClassName}>Bea</span>
      {/* Bold gold circle around capital C — AWS #FF9900 ring (thicker + slightly larger) */}
      <span
        className={`mx-[0.08em] inline-flex h-[1.28em] w-[1.28em] shrink-0 items-center justify-center rounded-full border-[2.5px] border-beacon-orange leading-none ${textClassName}`}
        aria-hidden="true"
      >
        <span className="translate-y-[0.02em] text-[0.76em] font-bold">C</span>
      </span>
      <span className={textClassName}>on</span>
    </span>
  )
}
